/* Gestão de staff no backoffice (ADR-0709). Só super_admin cria/gere operadores (o bootstrap
   só semeia o 1º). MVP sem migração de schema: senha inicial + reset (sem convite por e-mail).
   Guards de lockout (último super_admin ativo) e self-suspend são fail-closed no backend; a UI
   apenas esconde o self-suspend e traduz os 409. Molde de `companies-list-page`. */
import { STAFF_BULK_MAX_IDS, type StaffStatus, type StaffUserDto } from '@planuze/admin-contracts';
import {
  Button,
  ConfirmDialog,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useState } from 'react';
import { ChangeRoleDialog } from '../components/staff/change-role-dialog.js';
import { CreateStaffDialog } from '../components/staff/create-staff-dialog.js';
import { ResetPasswordDialog } from '../components/staff/reset-password-dialog.js';
import { StaffBulkBar } from '../components/staff/staff-bulk-bar.js';
import { staffErrorMessage } from '../components/staff/staff-error.js';
import { staffStatusLabel } from '../components/staff/staff-labels.js';
import { type StaffRowAction, StaffTable } from '../components/staff/staff-table.js';
import { useStaffContent } from '../content/sections/staff.js';
import { useBulkStaffStatus, useStaffList, useUpdateStaffStatus } from '../hooks/use-staff.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

type StatusTarget = { readonly staff: StaffUserDto; readonly next: StaffStatus };
type BulkStatusTarget = { readonly ids: readonly string[]; readonly status: StaffStatus };

export function StaffManagementPage() {
  const copy = useStaffContent();
  const session = useStaffSession();
  const isSuperAdmin = session?.role === 'super_admin';
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useStaffList();
  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const setStatus = useUpdateStaffStatus();
  const bulkStatus = useBulkStaffStatus();

  const [createOpen, setCreateOpen] = useState(false);
  const [roleTarget, setRoleTarget] = useState<StaffUserDto | null>(null);
  const [pwTarget, setPwTarget] = useState<StaffUserDto | null>(null);
  const [statusTarget, setStatusTarget] = useState<StatusTarget | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [pendingBulk, setPendingBulk] = useState<BulkStatusTarget | null>(null);

  const eligibleItems = items.filter((staff) => staff.id !== session?.id);
  const effectiveSelected = new Set(
    eligibleItems.filter((staff) => selectedIds.has(staff.id)).map((staff) => staff.id),
  );
  const toggle = (id: string): void =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else if (next.size < STAFF_BULK_MAX_IDS) next.add(id);
      return next;
    });
  const toggleAll = (checked: boolean): void =>
    setSelectedIds(
      checked
        ? new Set(eligibleItems.slice(0, STAFF_BULK_MAX_IDS).map((staff) => staff.id))
        : new Set(),
    );

  if (!isSuperAdmin) {
    return (
      <Page>
        <PageHeader>
          <PageTitleBlock>
            <PageTitle>{copy.pageTitle}</PageTitle>
            <PageDescription>{copy.restrictedDescription}</PageDescription>
          </PageTitleBlock>
        </PageHeader>
        <PageContent>
          <InlineAlert variant="destructive">{copy.restrictedAlert}</InlineAlert>
        </PageContent>
      </Page>
    );
  }

  const onAction = (staff: StaffUserDto, action: StaffRowAction): void => {
    if (action === 'role') setRoleTarget(staff);
    else if (action === 'reset-password') setPwTarget(staff);
    else if (action === 'suspend') setStatusTarget({ staff, next: 'suspended' });
    else setStatusTarget({ staff, next: 'active' });
  };

  /* Lança em erro: o ConfirmDialog exibe o InlineAlert (mapError) e permanece aberto —
     ex.: 409 last_super_admin ao suspender o último super_admin ativo. */
  const confirmStatus = async (): Promise<void> => {
    if (statusTarget === null) return;
    const { staff, next } = statusTarget;
    await setStatus.mutateAsync({ id: staff.id, status: next });
    toast.success(next === 'suspended' ? copy.suspendSuccessToast : copy.reactivateSuccessToast);
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
        <PageActions>
          <Button onClick={() => setCreateOpen(true)}>{copy.newMemberButton}</Button>
        </PageActions>
      </PageHeader>
      <PageContent className="space-y-4">
        <InlineAlert variant="info">{copy.infoAlert}</InlineAlert>
        <StaffBulkBar
          selectedCount={effectiveSelected.size}
          busy={bulkStatus.isPending}
          onSuspend={() => setPendingBulk({ ids: [...effectiveSelected], status: 'suspended' })}
          onReactivate={() => setPendingBulk({ ids: [...effectiveSelected], status: 'active' })}
          onClear={() => setSelectedIds(new Set())}
        />
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={6} />
        ) : (
          <StaffTable
            items={items}
            currentStaffId={session?.id ?? null}
            onAction={onAction}
            onCreate={() => setCreateOpen(true)}
            selectedIds={effectiveSelected}
            onToggle={toggle}
            onToggleAll={toggleAll}
          />
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {copy.loadMoreButton}
            </Button>
          </div>
        )}
      </PageContent>

      <CreateStaffDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      {roleTarget !== null && (
        <ChangeRoleDialog staff={roleTarget} onClose={() => setRoleTarget(null)} />
      )}
      {pwTarget !== null && (
        <ResetPasswordDialog staff={pwTarget} onClose={() => setPwTarget(null)} />
      )}
      <ConfirmDialog
        open={statusTarget !== null}
        onOpenChange={(open) => {
          if (!open) setStatusTarget(null);
        }}
        title={
          statusTarget?.next === 'suspended' ? copy.suspendDialogTitle : copy.reactivateDialogTitle
        }
        description={
          statusTarget !== null
            ? copy.confirmDescription(
                statusTarget.next === 'suspended'
                  ? copy.suspendActionLabel
                  : copy.reactivateActionLabel,
                statusTarget.staff.email,
                staffStatusLabel(statusTarget.staff.status),
              )
            : undefined
        }
        confirmLabel={
          statusTarget?.next === 'suspended' ? copy.suspendActionLabel : copy.reactivateActionLabel
        }
        variant={statusTarget?.next === 'suspended' ? 'destructive' : 'default'}
        mapError={staffErrorMessage}
        onConfirm={confirmStatus}
      />
      <ConfirmDialog
        open={pendingBulk !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBulk(null);
        }}
        title={
          pendingBulk?.status === 'suspended' ? copy.bulk.suspendTitle : copy.bulk.reactivateTitle
        }
        description={copy.bulk.description(
          pendingBulk?.status === 'suspended' ? copy.bulk.suspend : copy.bulk.reactivate,
          pendingBulk?.ids.length ?? 0,
        )}
        confirmLabel={
          pendingBulk?.status === 'suspended' ? copy.bulk.suspend : copy.bulk.reactivate
        }
        variant={pendingBulk?.status === 'suspended' ? 'destructive' : 'default'}
        mapError={staffErrorMessage}
        onConfirm={async () => {
          if (pendingBulk === null || pendingBulk.ids.length === 0) return;
          const response = await bulkStatus.mutateAsync({
            ids: [...pendingBulk.ids],
            status: pendingBulk.status,
          });
          const failed = response.results.filter((result) => !result.ok);
          if (failed.length === 0) toast.success(copy.bulk.success(response.results.length));
          else {
            toast.warning(
              copy.bulk.partial(response.results.length - failed.length, failed.length),
            );
          }
          setSelectedIds(new Set(failed.map((result) => result.id)));
          setPendingBulk(null);
        }}
      />
    </Page>
  );
}
